import { supabase } from './supabase'

export type PublicInspection = {
  inspectionId: string
  accessToken: string
  plate: string
  requirements: Array<{ code: string; label: string; required: boolean }>
  checklist: Array<{ code: string; label: string; required: boolean; category: string; sort_order: number; acceptanceCriteria?: string | null; recommendation?: string | null }>
  savedResults?: Record<string, { isCompliant: boolean; observation?: string | null }>
  evidenceCounts?: Record<string, number>
  evidenceStates?: Record<string, { count: number; status: 'approved' | 'observed' | 'pending'; reviewNote?: string | null }>
}

async function invoke<T>(name: string, body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, { body })
  if (error) throw error
  return data as T
}

export const beginByPlate = (plate: string) =>
  invoke<PublicInspection>('spence-public-inspection', { action: 'begin', plate })

export const getPublicChecklistHistory = (inspectionId: string, accessToken: string, checklistCode: string) =>
  invoke<RequirementHistory>('spence-public-inspection', { action: 'checklist_history', inspectionId, accessToken, checklistCode })

export const requestChecklistEvidenceUpload = (inspectionId: string, accessToken: string, checklistCode: string, file: File) =>
  invoke<{ bucket: string; path: string; token: string }>('spence-public-inspection', {
    action: 'create_evidence_upload', inspectionId, accessToken, checklistCode,
    fileName: file.name, contentType: file.type,
  })

export const registerChecklistEvidenceBatchUpload = (inspectionId: string, accessToken: string, checklistCode: string, files: Array<{ path: string; fileName: string; contentType: string }>) =>
  invoke<void>('spence-public-inspection', {
    action: 'finalize_evidence_batch', inspectionId, accessToken, checklistCode, files,
  })

export const submitInspection = (inspectionId: string, accessToken: string) =>
  invoke<void>('spence-public-inspection', { action: 'submit', inspectionId, accessToken })

export const saveChecklist = (inspectionId: string, accessToken: string, results: Array<{ code: string; observation?: string }>) =>
  invoke<void>('spence-public-inspection', { action: 'save_checklist', inspectionId, accessToken, results })

export const getInternalSession = () =>
  invoke<{ role: 'super_admin' | 'admin' | 'reviewer'; displayName: string }>('spence-inspection-action', { action: 'me' })

export const getReviewQueue = () =>
  invoke<{ items: Array<{ id: string; plate: string; status: string; submittedAt: string }> }>('spence-inspection-action', { action: 'queue' })

export type InspectionProgress = {
  vehicleId: string
  inspectionId?: string | null
  plate: string
  company: string
  equipmentType: string
  inspectionStatus?: string | null
  lastActivityAt?: string | null
  requirements: number
  touched: number
  approved: number
  progressPercent: number
  progressStatus: 'Sin movimiento' | 'En proceso' | 'Completado'
}

export type InspectionProgressFilters = { plate?: string; status?: string; dateFrom?: string; dateTo?: string }

export const getInspectionProgress = (page = 1, filters: InspectionProgressFilters = {}) =>
  invoke<{ items: InspectionProgress[]; total: number; page: number; pageSize: number; summary: { sinMovimiento: number; enProceso: number; completados: number } }>('spence-inspection-action', { action: 'inspection_progress', page, ...filters })

export type ReviewEvent = { type: string; at: string; actor?: string | null; actorType?: string | null; note?: string | null; status?: string | null; evidenceCount?: number | null }
export type ReviewEvidence = { evidenceId: string; path: string; mimeType?: string | null; uploadedAt: string; url?: string | null }
export type RequirementHistory = { evidences: ReviewEvidence[]; events: ReviewEvent[] }
export type ReviewDocument = { inspectionId: string; checklistItemId: string; plate: string; company: string; equipmentType: string; label: string; acceptanceCriteria?: string | null; recommendation?: string | null; inspectorObservation?: string | null; reviewStatus: 'pending' | 'approved' | 'observed'; reviewNote?: string | null; reviewedAt?: string | null; uploadedAt: string; evidenceCount: number; evidences: ReviewEvidence[]; events: ReviewEvent[] }
export type ReviewDashboard = { summary: { inspections: number; requirements: number; compliant: number; nonCompliant: number; reviewed: number; pending: number; documentsPending: number; documentsReviewed: number }; byCompany: Array<{ name: string; total: number; compliant: number; nonCompliant: number; reviewed: number }>; byEquipmentType: Array<{ name: string; total: number; compliant: number; nonCompliant: number; reviewed: number }> }

export const getReviewDocuments = () =>
  invoke<{ pending: ReviewDocument[]; reviewed: ReviewDocument[] }>('spence-inspection-action', { action: 'review_documents' })

export const getReviewDashboard = () =>
  invoke<ReviewDashboard>('spence-inspection-action', { action: 'review_dashboard' })

export const reviewRequirement = (inspectionId: string, checklistItemId: string, status: 'approved' | 'observed', note = '') =>
  invoke<void>('spence-inspection-action', { action: 'review_requirement', inspectionId, checklistItemId, status, note })

export const decideInspection = (inspectionId: string, decision: 'approved' | 'rejected', note = '') =>
  invoke<void>('spence-inspection-action', { action: 'review', inspectionId, decision, note })

export const getReviewDetail = <T>(inspectionId: string) =>
  invoke<T>('spence-inspection-action', { action: 'detail', inspectionId })

export const reviewChecklistItem = (inspectionId: string, checklistItemId: string, status: 'approved' | 'observed', note = '') =>
  invoke<void>('spence-inspection-action', { action: 'review_item', inspectionId, checklistItemId, status, note })

export const catalogAction = <T>(action: string, payload: Record<string, unknown> = {}) =>
  invoke<T>('spence-inspection-action', { action: `catalog_${action}`, payload })
